import React from "react";
import styled, { css } from "styled-components";
import { UI_CAPTION_SEMIBOLD_DF, icons } from "oolib";
import { t } from "../theme";

type IconName = keyof typeof icons;

export interface ButtonTertiaryCompactProps {
  id?: string;
  icon?: IconName;
  iconWeight?: "regular" | "bold" | "fill" | "light";
  iconColor?: string;
  color?: string;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  responsive?: boolean;
  style?: React.CSSProperties;
}

const compactPadding = (responsive: boolean, hasIcon: boolean) => {
  if (responsive) return hasIcon ? "8px 14px 8px 12px" : "8px 14px";
  return hasIcon ? "4px 10px 4px 8px" : "4px 10px";
};

const StyledButton = styled.button<{ $color: string; $responsive: boolean; $hasIcon: boolean }>`
  appearance: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background: transparent;
  color: ${({ $color }) => $color};
  border: none;
  box-shadow: inset 0 0 0 1px ${t("grey10")};
  border-radius: ${({ $responsive }) => ($responsive ? "18px" : "14px")};
  padding: ${({ $responsive, $hasIcon }) => compactPadding($responsive, $hasIcon)};
  transition: background 0.15s ease, color 0.15s ease;

  &:disabled {
    color: ${t("grey40")};
    pointer-events: none;
  }

  @media (hover: hover) {
    &:hover {
      background: ${t("grey5")};
    }
  }
`;

const Label = styled(UI_CAPTION_SEMIBOLD_DF)`
  ${css``}
`;

export const ButtonTertiaryCompact: React.FC<ButtonTertiaryCompactProps> = ({
  id,
  icon,
  iconWeight = "regular",
  iconColor,
  color,
  children,
  onClick,
  disabled,
  type = "button",
  responsive = false,
  style,
}) => {
  const resolvedColor = color || t("grey80");
  const IconComp = icon ? icons[icon] : null;
  const iconSize = responsive ? 16 : 14;

  return (
    <StyledButton
      id={id}
      type={type}
      onClick={onClick}
      disabled={disabled}
      $color={resolvedColor}
      $responsive={responsive}
      $hasIcon={!!icon}
      style={style}
    >
      {IconComp && (
        <IconComp
          size={iconSize}
          weight={iconWeight}
          color={disabled ? t("grey40") : iconColor || resolvedColor}
        />
      )}
      {children && <Label>{children}</Label>}
    </StyledButton>
  );
};

const StyledPrimaryButton = styled.button<{ $color: string; $responsive: boolean; $hasIcon: boolean }>`
  appearance: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background: ${t("primary")};
  color: ${({ $color }) => $color};
  border: none;
  border-radius: ${({ $responsive }) => ($responsive ? "18px" : "14px")};
  padding: ${({ $responsive, $hasIcon }) => compactPadding($responsive, $hasIcon)};
  transition: filter 0.15s ease;

  &:disabled {
    background: ${t("grey10")};
    color: ${t("grey40")};
    pointer-events: none;
  }

  @media (hover: hover) {
    &:hover {
      filter: brightness(0.92);
    }
  }
`;

export const ButtonPrimaryCompact: React.FC<ButtonTertiaryCompactProps> = ({
  id,
  icon,
  iconWeight = "regular",
  iconColor,
  color,
  children,
  onClick,
  disabled,
  type = "button",
  responsive = false,
  style,
}) => {
  const resolvedColor = color || t("onPrimary");
  const IconComp = icon ? icons[icon] : null;
  const iconSize = responsive ? 16 : 14;

  return (
    <StyledPrimaryButton
      id={id}
      type={type}
      onClick={onClick}
      disabled={disabled}
      $color={resolvedColor}
      $responsive={responsive}
      $hasIcon={!!icon}
      style={style}
    >
      {IconComp && (
        <IconComp
          size={iconSize}
          weight={iconWeight}
          color={disabled ? t("grey40") : iconColor || resolvedColor}
        />
      )}
      {children && <Label>{children}</Label>}
    </StyledPrimaryButton>
  );
};
